<template>
  <b-modal
    v-model="visible"
    centered
    size="lg"
    :title="messages.UPLOAD_PDF_MODAL_TITLE"
    modal-class="operaciones-pdf-modal"
    :no-close-on-backdrop="uploading"
    :no-close-on-esc="uploading"
    @hidden="handleHidden"
  >
    <div v-if="!result">
      <p class="text-grey mb-2">
        Selecciona o arrastra los archivos PDF correspondientes a las Órdenes de Compra (OC).
      </p>
      <p class="text-muted small mb-3">
        Cada archivo debe nombrarse con el número de OC (ejemplo: <code>4500123456.pdf</code>).
      </p>

      <!-- Zona de Drag and Drop -->
      <div
        class="pdf-dropzone p-4 text-center mb-3"
        :class="{ 'pdf-dropzone--active': isDragOver, 'pdf-dropzone--disabled': uploading }"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
        @click="triggerFileInput"
      >
        <font-awesome-icon icon="file-pdf" class="pdf-dropzone__icon mb-2" />
        <div class="font-weight-bold color-orange mb-1">
          Haz clic aquí o arrastra tus archivos PDF
        </div>
        <div class="text-muted small">
          Puedes seleccionar más de 100 archivos PDF a la vez (formato .pdf)
        </div>
        <input
          ref="fileInputRef"
          type="file"
          multiple
          accept=".pdf,application/pdf"
          class="d-none"
          :disabled="uploading"
          @change="onFileChange"
        />
      </div>

      <!-- Alerta de archivos no válidos -->
      <b-alert v-if="invalidCount > 0" show variant="warning" class="py-2 small">
        <font-awesome-icon icon="exclamation-triangle" class="mr-1" />
        Se omitieron {{ invalidCount }} archivo(s) porque no tienen formato .pdf.
      </b-alert>

      <!-- Resumen de archivos seleccionados -->
      <div v-if="selectedFiles.length > 0" class="mb-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div>
            <b-badge variant="info" class="mr-2 px-2 py-1">
              {{ selectedFiles.length }} archivo(s) listo(s)
            </b-badge>
            <span class="text-muted small">
              Peso total: {{ totalSizeMb }} MB
            </span>
          </div>
          <b-button
            variant="link"
            size="sm"
            class="text-danger p-0"
            :disabled="uploading"
            @click="clearFiles"
          >
            <font-awesome-icon icon="trash-alt" class="mr-1" />
            Limpiar lista
          </b-button>
        </div>

        <div class="pdf-preview-table-wrap">
          <table class="table table-sm table-hover mb-0">
            <thead class="thead-light">
              <tr>
                <th style="width: 50px;">#</th>
                <th>Nombre del Archivo</th>
                <th>N° OC Detectado</th>
                <th style="width: 90px;" class="text-right">Tamaño</th>
                <th style="width: 50px;" class="text-center"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in selectedFiles" :key="item.name + index">
                <td class="text-muted small">{{ index + 1 }}</td>
                <td class="text-truncate" style="max-width: 260px;" :title="item.name">
                  <font-awesome-icon icon="file-pdf" class="text-danger mr-1" />
                  {{ item.name }}
                </td>
                <td>
                  <b-badge :variant="item.numeroOc ? 'secondary' : 'danger'" class="font-weight-normal">
                    {{ item.numeroOc || "No es numérico" }}
                  </b-badge>
                </td>
                <td class="text-right small text-muted">
                  {{ formatBytes(item.size) }}
                </td>
                <td class="text-center">
                  <button
                    type="button"
                    class="btn btn-sm text-danger p-0"
                    :disabled="uploading"
                    title="Quitar de la lista"
                    @click.stop="removeFile(index)"
                  >
                    <font-awesome-icon icon="times" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Barra de progreso -->
      <div v-if="uploading" class="my-3">
        <div class="d-flex justify-content-between small text-muted mb-1">
          <span>{{ uploadStatusText || "Subiendo archivos a Google Cloud Storage..." }}</span>
          <span>{{ uploadProgress }}%</span>
        </div>
        <b-progress :value="uploadProgress" variant="warning" striped animated height="12px" />
      </div>
    </div>

    <div v-else>
      <b-alert
        :variant="result.fallidos === 0 ? 'success' : result.procesados > 0 ? 'warning' : 'danger'"
        show
        class="mb-3"
      >
        <div class="d-flex align-items-center font-weight-bold mb-1">
          <font-awesome-icon
            :icon="result.fallidos === 0 ? 'check-circle' : 'exclamation-triangle'"
            class="mr-2"
          />
          {{ result.mensaje || "Resultado de la carga masiva" }}
        </div>
        <div class="small">
          Total: <strong>{{ result.totalArchivos }}</strong> |
          Procesados con éxito: <strong>{{ result.procesados }}</strong> |
          Con error / No encontrados: <strong>{{ result.fallidos }}</strong>
        </div>
      </b-alert>

      <div class="d-flex justify-content-between align-items-center mb-2">
        <b-form-radio-group
          v-model="filterResult"
          size="sm"
          buttons
          button-variant="outline-secondary"
        >
          <b-form-radio value="all">Todos ({{ result.detalles.length }})</b-form-radio>
          <b-form-radio value="errors" :disabled="result.fallidos === 0">
            Errores ({{ result.fallidos }})
          </b-form-radio>
          <b-form-radio value="success" :disabled="result.procesados === 0">
            Exitosos ({{ result.procesados }})
          </b-form-radio>
        </b-form-radio-group>
      </div>

      <div class="pdf-preview-table-wrap mb-3">
        <table class="table table-sm table-hover mb-0">
          <thead class="thead-light">
            <tr>
              <th>Archivo</th>
              <th>N° OC</th>
              <th>Estado</th>
              <th>Mensaje</th>
              <th class="text-center">Enlace</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(det, i) in filteredResultDetails" :key="i">
              <td class="text-truncate" style="max-width: 220px;" :title="det.nombreArchivo">
                <font-awesome-icon icon="file-pdf" class="text-danger mr-1" />
                {{ det.nombreArchivo }}
              </td>
              <td>
                <code>{{ det.numeroOc }}</code>
              </td>
              <td>
                <b-badge :variant="det.success ? 'success' : 'danger'">
                  {{ det.success ? "Subido" : "Error" }}
                </b-badge>
              </td>
              <td class="small" :class="det.success ? 'text-muted' : 'text-danger'">
                {{ det.mensaje }}
              </td>
              <td class="text-center">
                <a
                  v-if="det.urlPdf"
                  :href="det.urlPdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn btn-sm btn-link p-0 text-danger"
                  title="Abrir PDF"
                >
                  <font-awesome-icon icon="eye" />
                </a>
                <span v-else class="text-muted">-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- FOOTER ESTÁNDAR DEL MODAL CON BOTONES PROMART -->
    <template #modal-footer>
      <div v-if="!result" class="d-flex justify-content-end align-items-center w-100">
        <b-button
          class="bg-grey-btn px-4 mr-2"
          :disabled="uploading"
          @click="visible = false"
        >
          Cancelar
        </b-button>
        <b-button
          class="bg-orange-btn px-4"
          :disabled="selectedFiles.length === 0 || uploading"
          @click="startUpload"
        >
          <b-spinner v-if="uploading" small class="mr-1" />
          <font-awesome-icon v-else icon="upload" class="mr-1" />
          {{ uploadButtonLabel }}
        </b-button>
      </div>

      <div v-else class="d-flex justify-content-end align-items-center w-100">
        <b-button class="bg-grey-btn px-3 mr-2" @click="resetToUpload">
          <font-awesome-icon icon="upload" class="mr-1" />
          Cargar más archivos
        </b-button>
        <b-button class="bg-orange-btn px-4" @click="handleFinish">
          <font-awesome-icon icon="check-circle" class="mr-1" />
          Finalizar
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { MESSAGES } from "@/services/operaciones/operaciones.constants";
import { cargarPdfOcMasivo } from "@/services/operaciones/operaciones.api";

function extraerNumeroOc(nombre) {
  if (!nombre) return "";
  const sinExt = nombre.replace(/\.[^/.]+$/, "").trim();

  // La OC debe contener únicamente dígitos numéricos
  if (/^\d+$/.test(sinExt)) {
    return sinExt;
  }

  return "";
}

export default {
  name: "OperacionesPdfUploadModal",

  props: {
    value: { type: Boolean, default: false },
    usuario: { type: String, default: "SISTEMA" },
  },

  data() {
    return {
      messages: MESSAGES,
      selectedFiles: [],
      invalidCount: 0,
      isDragOver: false,
      uploading: false,
      uploadProgress: 0,
      uploadStatusText: "",
      result: null,
      filterResult: "all",
    };
  },

  computed: {
    visible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    totalSizeMb() {
      const bytes = this.selectedFiles.reduce((acc, f) => acc + (f.size || 0), 0);
      return (bytes / (1024 * 1024)).toFixed(2);
    },

    filteredResultDetails() {
      if (!this.result?.detalles) return [];
      if (this.filterResult === "errors") {
        return this.result.detalles.filter((d) => !d.success);
      }
      if (this.filterResult === "success") {
        return this.result.detalles.filter((d) => d.success);
      }
      return this.result.detalles;
    },

    uploadButtonLabel() {
      if (this.uploading) return "Subiendo...";
      if (!this.selectedFiles.length) return "Subir PDFs";
      return `Subir ${this.selectedFiles.length} PDF(s)`;
    },
  },

  methods: {
    triggerFileInput() {
      if (!this.uploading && this.$refs.fileInputRef) {
        this.$refs.fileInputRef.click();
      }
    },

    onDragOver() {
      if (!this.uploading) {
        this.isDragOver = true;
      }
    },

    onDragLeave() {
      this.isDragOver = false;
    },

    onDrop(e) {
      this.isDragOver = false;
      if (this.uploading) return;
      const files = e.dataTransfer?.files;
      if (files && files.length) {
        this.processFiles(files);
      }
    },

    onFileChange(e) {
      const files = e.target?.files;
      if (files && files.length) {
        this.processFiles(files);
      }
      e.target.value = "";
    },

    processFiles(files) {
      let validList = [];
      let invalidCount = 0;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const isPdf =
          file.name.toLowerCase().endsWith(".pdf") ||
          file.type === "application/pdf";

        if (!isPdf) {
          invalidCount++;
          continue;
        }

        validList.push({
          file,
          name: file.name,
          size: file.size,
          numeroOc: extraerNumeroOc(file.name),
        });
      }

      this.invalidCount = invalidCount;
      // Combinar sin duplicados por nombre
      const mapExistentes = new Map(this.selectedFiles.map((f) => [f.name, f]));
      validList.forEach((f) => mapExistentes.set(f.name, f));
      this.selectedFiles = Array.from(mapExistentes.values());
    },

    removeFile(index) {
      this.selectedFiles.splice(index, 1);
    },

    clearFiles() {
      this.selectedFiles = [];
      this.invalidCount = 0;
    },

    async startUpload() {
      if (!this.selectedFiles.length || this.uploading) return;

      this.uploading = true;
      this.uploadProgress = 0;
      this.uploadStatusText = "Iniciando carga...";

      const MAX_BATCH_FILES = 5;
      const MAX_BATCH_BYTES = 6.5 * 1024 * 1024;
      const totalFiles = this.selectedFiles.length;
      const chunks = [];
      let currentChunk = [];
      let currentChunkBytes = 0;

      for (let i = 0; i < totalFiles; i++) {
        const item = this.selectedFiles[i];
        const fileSize = item.size || 0;

        if (
          currentChunk.length >= MAX_BATCH_FILES ||
          (currentChunk.length > 0 && currentChunkBytes + fileSize > MAX_BATCH_BYTES)
        ) {
          chunks.push(currentChunk);
          currentChunk = [];
          currentChunkBytes = 0;
        }

        currentChunk.push(item);
        currentChunkBytes += fileSize;
      }

      if (currentChunk.length > 0) {
        chunks.push(currentChunk);
      }

      const acumuladoDetalles = [];
      let totalProcesados = 0;
      let totalFallidos = 0;
      let huboErrorConexion = false;
      let ultimoMensajeError = "";
      let filesProcessedBefore = 0;

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const chunk = chunks[chunkIndex];
        const formData = new FormData();
        formData.append("usuario", this.usuario || "SISTEMA");

        chunk.forEach((item) => {
          formData.append("archivos", item.file, item.name);
        });

        const currentOffset = filesProcessedBefore;
        this.uploadStatusText =
          chunks.length > 1
            ? `Subiendo lote ${chunkIndex + 1} de ${chunks.length} (${chunk.length} archivos)...`
            : "Subiendo archivos a Google Cloud Storage...";

        try {
          const response = await cargarPdfOcMasivo(formData, (progressEvent) => {
            if (progressEvent.total > 0) {
              const chunkPercent = progressEvent.loaded / progressEvent.total;
              const overallPercent = Math.round(
                ((currentOffset + chunkPercent * chunk.length) / totalFiles) * 100
              );
              this.uploadProgress = Math.min(overallPercent, 99);
            }
          });

          if (response?.detalles && Array.isArray(response.detalles)) {
            acumuladoDetalles.push(...response.detalles);
          }
          totalProcesados += response?.procesados || 0;
          totalFallidos += response?.fallidos || 0;
        } catch (err) {
          huboErrorConexion = true;
          const msgError =
            err?.response?.data?.message ||
            err?.message ||
            "Error inesperado al conectar con el servidor";
          ultimoMensajeError = msgError;

          chunk.forEach((f) => {
            acumuladoDetalles.push({
              nombreArchivo: f.name,
              numeroOc: f.numeroOc,
              success: false,
              mensaje: msgError,
            });
            totalFallidos++;
          });
        } finally {
          filesProcessedBefore += chunk.length;
        }
      }

      this.uploadProgress = 100;
      this.uploadStatusText = "Carga finalizada";

      this.result = {
        success: totalFallidos === 0 && !huboErrorConexion,
        mensaje:
          totalFallidos === 0
            ? `Se procesaron correctamente ${totalProcesados} archivos PDF.`
            : `Proceso completado: ${totalProcesados} exitosos, ${totalFallidos} fallidos.${ultimoMensajeError ? " (" + ultimoMensajeError + ")" : ""}`,
        totalArchivos: totalFiles,
        procesados: totalProcesados,
        fallidos: totalFallidos,
        detalles: acumuladoDetalles,
      };

      if (totalProcesados > 0) {
        this.$emit("success");
      }
      this.uploading = false;
    },

    resetToUpload() {
      this.result = null;
      this.selectedFiles = [];
      this.invalidCount = 0;
      this.uploadProgress = 0;
      this.uploadStatusText = "";
      this.filterResult = "all";
    },

    handleFinish() {
      this.visible = false;
      this.resetToUpload();
    },

    handleHidden() {
      this.resetToUpload();
    },

    formatBytes(bytes) {
      if (!bytes || bytes === 0) return "0 B";
      const k = 1024;
      const dm = 1;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    },
  },
};
</script>

<style scoped>
.pdf-dropzone {
  border: 2px dashed #ff7a00;
  border-radius: 8px;
  background-color: #fff9f5;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pdf-dropzone:hover,
.pdf-dropzone--active {
  background-color: #fff0e6;
  border-color: #e56d00;
}

.pdf-dropzone--disabled {
  pointer-events: none;
  opacity: 0.6;
}

.pdf-dropzone__icon {
  font-size: 2.2rem;
  color: #ff7a00;
}

.pdf-preview-table-wrap {
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.bg-orange-btn:disabled {
  background-color: #ffd6b3 !important;
  border-color: #ffd6b3 !important;
  color: #ffffff !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
  opacity: 1 !important;
}

.bg-grey-btn:disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}
</style>
